/**
 * The taxonomy editor: what this household rates, and the words each rating is
 * made of.
 *
 * Two things shape the layout. First, the stop labels are the product — a stop
 * that says "3" is the numeric slider this app exists to avoid — so the editor
 * puts the live control next to the words being typed rather than behind a
 * preview button. Second, nothing here is ever deleted to tidy up: the archive
 * action sits above the delete, and the delete refuses itself the moment a
 * session snapshot refers to the row.
 *
 * All the rules live in `logic.ts`. This file is the arrangement.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { Stars } from "../../components/Stars.tsx";
import { canonicalisePatch, useLocale } from "../../locale.ts";
import type { ScreenProps } from "../../main.tsx";
import type { Aspect, AspectKind, Polarity, Stop, SubQuestion } from "../../../shared/types.ts";
import { createRow, tombstone, useAspects, useSubQuestions, useUsage, write } from "./data.ts";
import {
  addStopAt,
  archivePatch,
  aspectPatchFrom,
  bySort,
  disposalOptions,
  draftFromAspect,
  draftFromSubQuestion,
  draftProblems,
  draftStops,
  HISTORY_SAFE_NOTE,
  isArchived,
  KIND_COPY,
  MAX_STOPS,
  MIN_STOPS,
  newAspectDraft,
  newSubQuestionDraft,
  nextSort,
  POLARITY_COPY,
  removeStopAt,
  renumberStops,
  reorder,
  restorePatch,
  setLabelAt,
  STABLE_COPY,
  STARRED_COPY,
  subQuestionPatchFrom,
  subQuestionProblems,
} from "./logic.ts";
import type { AspectDraft, SubQuestionDraft } from "./logic.ts";
import {
  Banner,
  ConfirmButton,
  Empty,
  Note,
  Problems,
  Row,
  Section,
  Segmented,
  SettingsScreen,
  Switch,
  TextField,
} from "./ui.tsx";
import type { SegmentOption } from "./ui.tsx";

const KIND_OPTIONS: readonly SegmentOption<AspectKind>[] = [
  { value: "DESCRIPTIVE", copy: KIND_COPY.DESCRIPTIVE },
  { value: "EVALUATIVE", copy: KIND_COPY.EVALUATIVE },
];

const POLARITY_OPTIONS: readonly SegmentOption<Polarity>[] = [
  { value: "BIPOLAR", copy: POLARITY_COPY.BIPOLAR },
  { value: "UNIPOLAR", copy: POLARITY_COPY.UNIPOLAR },
];

/** A failed write reported in the caller's own words, never swallowed. */
type Run = (fn: () => Promise<unknown>) => void;

type Editing =
  | { mode: "new"; draft: AspectDraft }
  | { mode: "edit"; id: string; draft: AspectDraft };

type SubEditing =
  | { mode: "new"; draft: SubQuestionDraft }
  | { mode: "edit"; id: string; draft: SubQuestionDraft };

// ---------------------------------------------------------------------------
// Stop editor
// ---------------------------------------------------------------------------

/**
 * The ladder, and the thing the ladder becomes.
 *
 * The stars below the inputs are the real control, not a mock-up. What they are
 * no longer is a preview of *these labels*: a rating is five stars tapped in
 * halves whatever the rubric says. The labels are the words the results screens
 * read a rating back with, so the preview shows the scale a rater is handed and
 * the list above it is the vocabulary that scale gets described in.
 */
function StopEditor(props: {
  legend: string;
  name: string;
  stops: readonly Stop[];
  labels: readonly string[];
  onChange: (labels: string[]) => void;
}): ReactNode {
  const { legend, name, stops, labels, onChange } = props;
  const [preview, setPreview] = useState<number | null>(null);

  // The scale no longer depends on the rubric, so editing the labels can no
  // longer strand a previewed value the way removing a stop used to.
  const previewValue = preview;
  const atMax = labels.length >= MAX_STOPS;
  const atMin = labels.length <= MIN_STOPS;

  return (
    <div className="pb-stops">
      <div className="pb-row">
        <span className="pb-field__label">{legend}</span>
        <span className="pb-stops__count">
          {labels.length} of {MIN_STOPS}–{MAX_STOPS}
        </span>
      </div>

      {labels.map((label, i) => (
        // Index keys: a stop has no identity beyond its position, and its text
        // is free to be blank or duplicated mid-edit.
        <div className="pb-stops__row" key={i}>
          <span className="pb-stops__value">{stops[i]?.value ?? ""}</span>
          <input
            className="pb-input"
            type="text"
            value={label}
            aria-label={`Stop ${i + 1} label`}
            placeholder="Name the state, not the number"
            data-invalid={label.trim() === "" ? "true" : undefined}
            onChange={(e) => onChange(setLabelAt(labels, i, e.currentTarget.value))}
          />
          <button
            type="button"
            className="pb-iconbtn"
            aria-label={`Add a stop after stop ${i + 1}`}
            title={atMax ? `A rubric stops at ${MAX_STOPS}.` : "Add a stop below"}
            disabled={atMax}
            onClick={() => onChange(addStopAt(labels, i))}
          >
            +
          </button>
          <button
            type="button"
            className="pb-iconbtn"
            aria-label={`Remove stop ${i + 1}`}
            title={atMin ? `A rubric needs at least ${MIN_STOPS}.` : "Remove this stop"}
            disabled={atMin}
            onClick={() => onChange(removeStopAt(labels, i))}
          >
            −
          </button>
        </div>
      ))}

      <Note>{HISTORY_SAFE_NOTE}</Note>

      <div className="pb-preview">
        <Stars value={previewValue} onChange={setPreview} aspectName={name} />
        <span className="pb-quiet">Try it — this is exactly what a rater is handed.</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aspect editor
// ---------------------------------------------------------------------------

function AspectEditor(props: {
  draft: AspectDraft;
  saveLabel: string;
  onChange: (draft: AspectDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}): ReactNode {
  const { draft, onChange } = props;
  const problems = draftProblems(draft);

  return (
    <div className="pb-tile" data-sunk="true" data-edge="spice">
      <div className="pb-stack">
        <TextField
          label="Name"
          value={draft.name}
          onChange={(name) => onChange({ ...draft, name })}
          placeholder="Luck ↔ Strategy"
          invalid={draft.name.trim() === ""}
        />

        <Segmented
          label="Kind"
          value={draft.kind}
          options={KIND_OPTIONS}
          onChange={(kind) => onChange({ ...draft, kind })}
        />

        <Segmented
          label="Scale"
          value={draft.polarity}
          options={POLARITY_OPTIONS}
          onChange={(polarity) => onChange({ ...draft, polarity })}
        />

        <Switch
          label={STABLE_COPY.title}
          hint={STABLE_COPY.line}
          checked={draft.stable === 1}
          onChange={(on) => onChange({ ...draft, stable: on ? 1 : 0 })}
        />

        <Switch
          label={STARRED_COPY.title}
          hint={STARRED_COPY.line}
          checked={draft.starred === 1}
          onChange={(on) => onChange({ ...draft, starred: on ? 1 : 0 })}
        />

        <StopEditor
          legend="Stops"
          name={draft.name.trim() === "" ? "New aspect" : draft.name.trim()}
          stops={draftStops(draft)}
          labels={draft.labels}
          onChange={(labels) => onChange({ ...draft, labels })}
        />

        <Problems items={problems} />

        <div className="pb-actions">
          <button
            type="button"
            className="pb-btn"
            data-variant="primary"
            disabled={problems.length > 0}
            onClick={props.onSave}
          >
            {props.saveLabel}
          </button>
          <button type="button" className="pb-btn" data-variant="quiet" onClick={props.onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-questions
//
// Off by default in this product, so they live behind a disclosure on the
// aspect they belong to rather than on the top-level list.
// ---------------------------------------------------------------------------

/**
 * Whether an aspect's row carries its follow-ups.
 *
 * The disclosure used to be rendered only inside the aspect *editor*, which made
 * a follow-up something you could only reach by opening an aspect for editing
 * and scrolling past the whole rubric — the name, the kind, the scale, both
 * switches and every stop. So a group who added "Turn pressure" under Tension
 * met it again in the night and then could not find it anywhere in settings,
 * because nothing on the list they were looking at admitted it existed.
 *
 * Silent for an aspect with none, unless that aspect is open: the list is not
 * the place to advertise a feature most groups never use, but it is exactly the
 * place to say that a follow-up somebody wrote is still there. The editor is
 * still where a first one is added, which is what the second half is for.
 */
export function showsFollowUps(rows: readonly SubQuestion[], editing: boolean): boolean {
  return rows.length > 0 || editing;
}

export function SubQuestions(props: {
  aspect: Aspect;
  rows: readonly SubQuestion[];
  run: Run;
}): ReactNode {
  const { aspect, rows, run } = props;
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<SubEditing | null>(null);
  const locale = useLocale();

  const live = bySort(rows.filter((row) => !isArchived(row)));
  const archived = bySort(rows.filter((row) => isArchived(row)));

  const save = (): void => {
    if (!editing) return;
    if (subQuestionProblems(editing.draft, aspect.polarity).length > 0) return;
    run(async () => {
      if (editing.mode === "new") {
        await createRow("subquestion", subQuestionPatchFrom(editing.draft, aspect));
      } else {
        const previous = rows.find((row) => row.id === editing.id);
        if (!previous) return;
        await write(
          "subquestion",
          previous.id,
          canonicalisePatch(
            subQuestionPatchFrom(editing.draft, aspect, previous),
            previous.seed_key,
            locale,
          ),
          previous.version,
        );
      }
      setEditing(null);
    });
  };

  return (
    <div className="pb-stack" data-gap="tight">
      <div className="pb-row">
        <button
          type="button"
          className="pb-btn"
          data-variant="quiet"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {open ? "Hide" : "Show"} sub-questions ({live.length})
        </button>
        <span className="pb-quiet">Optional. Most groups never add any.</span>
      </div>

      {open && (
        <>
          {live.length === 0 && editing === null && (
            <Empty>Nothing under {aspect.name}. It reads fine on its own.</Empty>
          )}

          {live.length > 0 && (
            <ul className="pb-list">
              {live.map((sub) => (
                <li key={sub.id}>
                  <div className="pb-item">
                    <div className="pb-item__main">
                      <span className="pb-item__name">{sub.name}</span>
                      <span className="pb-item__meta">{sub.stops.length} stops</span>
                    </div>
                    <button
                      type="button"
                      className="pb-iconbtn"
                      aria-label={`Edit ${sub.name}`}
                      onClick={() =>
                        setEditing({ mode: "edit", id: sub.id, draft: draftFromSubQuestion(sub) })
                      }
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className="pb-btn"
                      data-variant="quiet"
                      onClick={() =>
                        run(() => write("subquestion", sub.id, archivePatch(), sub.version))
                      }
                    >
                      Archive
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {editing !== null && (
            <div className="pb-tile" data-sunk="true" data-edge="muted">
              <div className="pb-stack">
                <TextField
                  label="Sub-question"
                  value={editing.draft.name}
                  onChange={(name) => setEditing({ ...editing, draft: { ...editing.draft, name } })}
                  placeholder="Something narrower than the aspect"
                  invalid={editing.draft.name.trim() === ""}
                />
                <StopEditor
                  legend="Stops"
                  name={
                    editing.draft.name.trim() === "" ? "New sub-question" : editing.draft.name.trim()
                  }
                  stops={renumberStops(editing.draft.labels, aspect.polarity)}
                  labels={editing.draft.labels}
                  onChange={(labels) =>
                    setEditing({ ...editing, draft: { ...editing.draft, labels } })
                  }
                />
                <Problems items={subQuestionProblems(editing.draft, aspect.polarity)} />
                <div className="pb-actions">
                  <button
                    type="button"
                    className="pb-btn"
                    data-variant="primary"
                    disabled={subQuestionProblems(editing.draft, aspect.polarity).length > 0}
                    onClick={save}
                  >
                    {editing.mode === "new" ? "Add sub-question" : "Save sub-question"}
                  </button>
                  <button
                    type="button"
                    className="pb-btn"
                    data-variant="quiet"
                    onClick={() => setEditing(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {editing === null && (
            <div className="pb-actions">
              <button
                type="button"
                className="pb-btn"
                data-variant="quiet"
                onClick={() =>
                  setEditing({ mode: "new", draft: newSubQuestionDraft(aspect, nextSort(rows)) })
                }
              >
                New sub-question
              </button>
            </div>
          )}

          {archived.length > 0 && (
            <ul className="pb-list">
              {archived.map((sub) => (
                <li key={sub.id}>
                  <div className="pb-item" data-archived="true">
                    <div className="pb-item__main">
                      <span className="pb-item__name">{sub.name}</span>
                      <span className="pb-item__meta">Archived</span>
                    </div>
                    <button
                      type="button"
                      className="pb-btn"
                      data-variant="quiet"
                      onClick={() =>
                        run(() => write("subquestion", sub.id, restorePatch(), sub.version))
                      }
                    >
                      Restore
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export function Aspects(props: ScreenProps): ReactNode {
  const { rows, loading, error } = useAspects();
  const subQuestions = useSubQuestions();
  const usage = useUsage("session_aspect", "aspect_id");
  const locale = useLocale();

  const [failure, setFailure] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  // `?aspect=<id>` deep-links here from a session or a game page.
  const [pendingOpen, setPendingOpen] = useState<string | null>(() => props.query.get("aspect"));

  const run = useCallback<Run>((fn) => {
    void (async () => {
      setFailure(null);
      try {
        await fn();
      } catch (err) {
        setFailure(err instanceof Error ? err.message : String(err));
      }
    })();
  }, []);

  const live = useMemo(() => bySort(rows.filter((row) => !isArchived(row))), [rows]);
  const archived = useMemo(() => bySort(rows.filter((row) => isArchived(row))), [rows]);

  useEffect(() => {
    if (pendingOpen === null || loading) return;
    const target = rows.find((row) => row.id === pendingOpen);
    setPendingOpen(null);
    if (target) setEditing({ mode: "edit", id: target.id, draft: draftFromAspect(target) });
  }, [pendingOpen, loading, rows]);

  const move = (id: string, delta: -1 | 1): void => {
    const changes = reorder(live, id, delta);
    run(async () => {
      // Serial, not parallel: each write carries the version it was read at, and
      // two of them landing at once is how a reorder turns into a conflict.
      for (const change of changes) {
        await write("aspect", change.id, { sort: change.sort }, change.version);
      }
    });
  };

  const saveAspect = (): void => {
    if (!editing) return;
    if (draftProblems(editing.draft).length > 0) return;
    run(async () => {
      if (editing.mode === "new") {
        await createRow("aspect", aspectPatchFrom(editing.draft));
      } else {
        const previous = rows.find((row) => row.id === editing.id);
        if (!previous) return;
        await write(
          "aspect",
          previous.id,
          canonicalisePatch(aspectPatchFrom(editing.draft, previous), previous.seed_key, locale),
          previous.version,
        );
      }
      setEditing(null);
    });
  };

  return (
    <SettingsScreen
      title="Aspects"
      subtitle="What you rate, and the words every rating is made of."
      back={{ to: "/settings", label: "Settings" }}
    >
      {error !== null && (
        <Banner tone="danger" title="Cannot read your aspects">
          {error}
        </Banner>
      )}
      {failure !== null && (
        <Banner
          tone="danger"
          title="That change did not save"
          actions={
            <button
              type="button"
              className="pb-btn"
              data-variant="quiet"
              onClick={() => setFailure(null)}
            >
              Dismiss
            </button>
          }
        >
          {failure}
        </Banner>
      )}

      <Section
        title="In use"
        hint="Starred aspects are offered the moment a session starts. Everything else stays one tap away."
        aside={
          <button
            type="button"
            className="pb-btn"
            data-variant="primary"
            disabled={editing !== null}
            onClick={() => setEditing({ mode: "new", draft: newAspectDraft(nextSort(rows)) })}
          >
            New aspect
          </button>
        }
      >
        {editing?.mode === "new" && (
          <AspectEditor
            draft={editing.draft}
            saveLabel="Add aspect"
            onChange={(draft) => setEditing({ mode: "new", draft })}
            onSave={saveAspect}
            onCancel={() => setEditing(null)}
          />
        )}

        {loading ? (
          <Empty>Reading your taxonomy…</Empty>
        ) : live.length === 0 ? (
          <Empty>No aspects yet. The headline score works without them.</Empty>
        ) : (
          <ul className="pb-list">
            {live.map((aspect, index) => {
              const open = editing?.mode === "edit" && editing.id === aspect.id;
              const used = usage.get(aspect.id) ?? 0;
              const { destroy } = disposalOptions({ sessions: used });
              const mine = subQuestions.rows.filter((sub) => sub.aspect_id === aspect.id);

              return (
                <li key={aspect.id}>
                  <div className="pb-item">
                    <button
                      type="button"
                      className="pb-iconbtn"
                      aria-label={`Move ${aspect.name} up`}
                      disabled={index === 0 || editing !== null}
                      onClick={() => move(aspect.id, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="pb-iconbtn"
                      aria-label={`Move ${aspect.name} down`}
                      disabled={index === live.length - 1 || editing !== null}
                      onClick={() => move(aspect.id, 1)}
                    >
                      ↓
                    </button>

                    <div className="pb-item__main">
                      <span className="pb-item__name">
                        {aspect.starred === 1 && (
                          <span className="pb-star" aria-hidden="true">
                            ★
                          </span>
                        )}
                        {aspect.name}
                        {aspect.starred === 1 && (
                          <span className="pb-tag" data-tone="accent">
                            Default set
                          </span>
                        )}
                        <span className="pb-tag">{KIND_COPY[aspect.kind].title}</span>
                        <span className="pb-tag">{POLARITY_COPY[aspect.polarity].title}</span>
                        {aspect.stable === 1 && <span className="pb-tag">Once per game</span>}
                      </span>
                      <span className="pb-item__meta">
                        {aspect.stops.length} stops · rubric v{aspect.rubric_version}
                        {used > 0 && ` · in ${used} session${used === 1 ? "" : "s"}`}
                      </span>
                    </div>

                    <button
                      type="button"
                      className="pb-btn"
                      data-variant="quiet"
                      aria-expanded={open}
                      disabled={editing !== null && !open}
                      onClick={() =>
                        setEditing(
                          open
                            ? null
                            : { mode: "edit", id: aspect.id, draft: draftFromAspect(aspect) },
                        )
                      }
                    >
                      {open ? "Close" : "Edit"}
                    </button>
                  </div>

                  {/* On the row, not inside the editor — see `showsFollowUps`.
                      Above the editor rather than below it, so an aspect's own
                      follow-ups stay next to the aspect however far the rubric
                      underneath them has been scrolled. */}
                  {showsFollowUps(mine, open) && (
                    <SubQuestions aspect={aspect} rows={mine} run={run} />
                  )}

                  {open && (
                    <div className="pb-stack">
                      <AspectEditor
                        draft={editing.draft}
                        saveLabel="Save aspect"
                        onChange={(draft) => setEditing({ mode: "edit", id: aspect.id, draft })}
                        onSave={saveAspect}
                        onCancel={() => setEditing(null)}
                      />

                      <Row
                        label="Archive"
                        hint="Leaves the pickers. Stays in every session that used it."
                      >
                        <button
                          type="button"
                          className="pb-btn"
                          data-variant="quiet"
                          onClick={() => {
                            setEditing(null);
                            run(() =>
                              write("aspect", aspect.id, archivePatch(), aspect.version),
                            );
                          }}
                        >
                          Archive
                        </button>
                      </Row>

                      <Row
                        label="Delete permanently"
                        hint="Only ever offered for a row nothing in the record points at."
                      >
                        <ConfirmButton
                          label="Delete"
                          title={`Delete ${aspect.name}?`}
                          consequences={[
                            "The aspect and its wording are removed from every device.",
                            mine.length > 0
                              ? `Its ${mine.length} sub-question${mine.length === 1 ? "" : "s"} go with it.`
                              : "It has no sub-questions.",
                            "No past session referred to it, so no review changes.",
                          ]}
                          confirmLabel="Delete for good"
                          variant="danger"
                          disabled={!destroy.allowed}
                          disabledReason={destroy.blocked_because}
                          onConfirm={() => {
                            setEditing(null);
                            run(async () => {
                              // A session snapshot of a sub-question cannot exist
                              // without one of its aspect, so `destroy.allowed`
                              // covers the children too.
                              for (const sub of mine) {
                                await tombstone("subquestion", sub.id, sub.version);
                              }
                              await tombstone("aspect", aspect.id, aspect.version);
                            });
                          }}
                        />
                      </Row>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      {archived.length > 0 && (
        <Section
          title="Archived"
          hint="Out of the pickers, still part of every session that used them."
          aside={
            <button
              type="button"
              className="pb-btn"
              data-variant="quiet"
              aria-expanded={showArchived}
              onClick={() => setShowArchived(!showArchived)}
            >
              {showArchived ? "Hide" : `Show ${archived.length}`}
            </button>
          }
        >
          {showArchived && (
            <ul className="pb-list">
              {archived.map((aspect) => {
                const mine = subQuestions.rows.filter((sub) => sub.aspect_id === aspect.id);
                return (
                  <li key={aspect.id}>
                    <div className="pb-item" data-archived="true">
                      <div className="pb-item__main">
                        <span className="pb-item__name">{aspect.name}</span>
                        <span className="pb-item__meta">
                          {KIND_COPY[aspect.kind].title} · {aspect.stops.length} stops · in{" "}
                          {usage.get(aspect.id) ?? 0} sessions
                          {/* Counted, not opened. Archiving an aspect takes its
                              follow-ups out of the pickers with it, so there is
                              nothing here to manage until the aspect comes back
                              — but a follow-up somebody wrote has to be findable
                              from the list it disappeared from, and this section
                              reads and restores rather than edits. */}
                          {mine.length > 0 &&
                            ` · ${mine.length} follow-up${mine.length === 1 ? "" : "s"}`}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="pb-btn"
                        data-variant="quiet"
                        onClick={() =>
                          run(() => write("aspect", aspect.id, restorePatch(), aspect.version))
                        }
                      >
                        Restore
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>
      )}
    </SettingsScreen>
  );
}
